import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import React from "react";
import { FiEdit2, FiMinus, FiPlus } from "react-icons/fi";
import ConfirmDialog from "../ConfirmDialog";
import { isChartCellEditable, replacePlaceholders } from "../layout/documentLayout";
import type { AnswersByQuestion, ContentItem } from "../layout/documentLayout";
import { firstAnswer, replaceInAnswer } from "./answerFormat";
import type { AnswerWrite } from "./answerWrites";
import { canBeChildOfChart, formatChartCell } from "./blockFormat";
import { answersForRow, editorChartRows, nextChartRow, removeLastRowPlan } from "./chartRows";
import { useAnswerWriter } from "./editorValues";
import type { QuestionEditorProps, QuestionTypeDefinition } from "./types";

// Editor of a chart: its rows as a table, one column per chart column. A cell opens its own
// type's editor below the row and saves at that row (`chartRow` = the row index). Entity-backed
// types (and types without an editor) stay view-only. Rows are the stored ones, gaps included
// (e.g. rows 0 and 2); "Add row" adds an empty row in the editor only, which exists once one of
// its cells is saved. "Remove last row" (confirmed first when the row has answers) deletes every
// answer of the last row, nested ones too (chartRows.ts), one write at a time; on a failure Retry
// continues with the answers left.

interface Props extends QuestionEditorProps {
  /** The question-type registry (passed in, as the registry itself hosts this editor). */
  getQuestionType: (itemType: ContentItem["itemType"]) => QuestionTypeDefinition;
}

interface OpenCell {
  questionId: number;
  row: number;
}

function CellValue(props: {
  cell: ContentItem;
  getQuestionType: Props["getQuestionType"];
  rowAnswers: AnswersByQuestion;
  viewProps: Pick<QuestionEditorProps, "byId" | "layoutType" | "placeholders">;
}) {
  const { cell, rowAnswers } = props;
  const cellAnswers = rowAnswers.get(cell.id) ?? [];

  if (canBeChildOfChart(cell.itemType)) {
    // The text the preview table and the PDF print
    const text = formatChartCell(
      replaceInAnswer(firstAnswer(cellAnswers), props.viewProps.placeholders),
      cell.itemType
    );
    return text === "" ? (
      <Text as="span" color="gray.400">
        —
      </Text>
    ) : (
      <Text as="span" whiteSpace="pre-wrap">
        {text}
      </Text>
    );
  }

  const { View } = props.getQuestionType(cell.itemType);
  return (
    <View
      answers={rowAnswers}
      block={{ question: cell, answers: cellAnswers, width: null }}
      byId={props.viewProps.byId}
      layoutType={props.viewProps.layoutType}
      placeholders={props.viewProps.placeholders}
    />
  );
}

export function ChartQuestionEditor(props: Props) {
  const { answers, getQuestionType, placeholders, question: chart } = props;
  const columns = chart.columns?.[0] ?? [];
  const [addedRows, setAddedRows] = React.useState<number[]>([]);
  const [openCell, setOpenCell] = React.useState<OpenCell | null>(null);
  // What the last save was, so its error (and spinner) shows next to it
  const [lastSave, setLastSave] = React.useState<"cell" | "removeRow" | null>(null);
  const removeWriter = useAnswerWriter(props.onSaveAnswers);

  const rows = editorChartRows(chart, answers, addedRows);
  const removal = removeLastRowPlan(chart, answers, rows);
  // Removing a row that holds answers deletes them: confirmed first (an empty row goes at once)
  const [isConfirmingRemove, setIsConfirmingRemove] = React.useState(false);
  const isBusy = props.isSaving || openCell !== null;

  const cellEditor = (cell: ContentItem) => {
    if (!isChartCellEditable(cell)) {
      return null;
    }
    return getQuestionType(cell.itemType).Editor;
  };

  const openCellEditor = (cell: OpenCell | null) => {
    removeWriter.reset();
    setLastSave(null);
    setOpenCell(cell);
  };

  const addRow = () => {
    const row = nextChartRow(rows);
    setAddedRows((prev) => [...prev, row]);
    const firstEditable = columns.find((cell) => cellEditor(cell) !== null);
    openCellEditor(firstEditable !== undefined ? { questionId: firstEditable.id, row } : null);
  };

  const removeLastRow = async () => {
    if (removal === null) {
      return;
    }
    setLastSave("removeRow");
    const isRemoved = removal.writes.length === 0 || (await removeWriter.run(removal.writes));
    if (isRemoved) {
      setLastSave(null);
      setAddedRows((prev) => prev.filter((row) => row !== removal.row));
    }
  };

  const saveCell = async (writes: AnswerWrite[]) => {
    setLastSave("cell");
    try {
      await props.onSaveAnswers(writes);
    } catch {
      // Shown in the cell's editor; its Retry saves again
      return;
    }
    setLastSave(null);
    setOpenCell(null);
  };

  const renderOpenCell = (cell: ContentItem, row: number, rowAnswers: AnswersByQuestion) => {
    const Editor = cellEditor(cell);
    if (Editor === null) {
      return null;
    }
    return (
      <Editor
        answers={rowAnswers}
        byId={props.byId}
        chartRow={row}
        error={lastSave === "cell" ? props.error : null}
        isSaving={props.isSaving}
        layoutType={props.layoutType}
        placeholders={placeholders}
        question={cell}
        value={firstAnswer(rowAnswers.get(cell.id) ?? []) ?? null}
        onCancel={() => {
          if (!props.isSaving) {
            openCellEditor(null);
          }
        }}
        onClose={() => openCellEditor(null)}
        onSave={(value) => void saveCell([{ questionId: cell.id, chartRow: row, answer: value }])}
        onSaveAnswers={(writes) => {
          setLastSave("cell");
          return props.onSaveAnswers(writes);
        }}
      />
    );
  };

  return (
    <Box
      bg="white"
      borderColor="gray.200"
      borderRadius="md"
      borderWidth="1px"
      display="flex"
      flexDirection="column"
      gap={3}
      p={3}
    >
      <Text fontSize="sm" fontWeight="semibold">
        {replacePlaceholders(chart.label, placeholders)}
      </Text>

      {columns.length === 0 ? (
        <Text color="gray.500" fontSize="sm">
          This chart has no columns.
        </Text>
      ) : (
        <TableContainer borderColor="gray.100" borderRadius="md" borderWidth="1px">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th w="1%">#</Th>
                {columns.map((cell) => (
                  <Th key={cell.id} textTransform="none">
                    {replacePlaceholders(cell.label, placeholders)}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, rowIndex) => {
                const rowAnswers = answersForRow(answers, row);
                const openInRow =
                  openCell?.row === row
                    ? columns.find((cell) => cell.id === openCell.questionId)
                    : undefined;
                return (
                  <React.Fragment key={row}>
                    <Tr bg={openInRow !== undefined ? "blue.50" : undefined}>
                      <Td color="gray.500" verticalAlign="top">
                        {rowIndex + 1}
                      </Td>
                      {columns.map((cell) => {
                        const isEditable = cellEditor(cell) !== null;
                        const isOpen = openInRow?.id === cell.id;
                        return (
                          <Td
                            key={cell.id}
                            bg={isOpen ? "blue.100" : undefined}
                            verticalAlign="top"
                            whiteSpace="normal"
                          >
                            <Flex alignItems="flex-start" gap={2} justifyContent="space-between">
                              <Box fontSize="sm" minW={0}>
                                <CellValue
                                  cell={cell}
                                  getQuestionType={getQuestionType}
                                  rowAnswers={rowAnswers}
                                  viewProps={{
                                    byId: props.byId,
                                    layoutType: props.layoutType,
                                    placeholders,
                                  }}
                                />
                              </Box>
                              {isEditable && (
                                <IconButton
                                  aria-label={`Edit ${cell.label}, row ${rowIndex + 1}`}
                                  icon={<FiEdit2 />}
                                  isDisabled={isBusy}
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => openCellEditor({ questionId: cell.id, row })}
                                />
                              )}
                            </Flex>
                          </Td>
                        );
                      })}
                    </Tr>
                    {openInRow !== undefined && (
                      <Tr>
                        <Td bg="blue.50" colSpan={columns.length + 1} whiteSpace="normal">
                          {renderOpenCell(openInRow, row, rowAnswers)}
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {lastSave === "removeRow" && props.error !== null && (
        <Alert alignItems="center" borderRadius="md" gap={2} py={2} status="error">
          <AlertIcon />
          <AlertDescription flex={1} fontSize="sm">
            Could not remove the last row. {props.error}
          </AlertDescription>
          <Button
            colorScheme="red"
            isLoading={props.isSaving}
            size="sm"
            variant="outline"
            onClick={() => void removeLastRow()}
          >
            Retry
          </Button>
        </Alert>
      )}

      <Flex gap={2} wrap="wrap">
        <Button
          isDisabled={isBusy || columns.length === 0}
          leftIcon={<FiPlus />}
          size="sm"
          variant="outline"
          onClick={addRow}
        >
          Add row
        </Button>
        <Button
          colorScheme="red"
          isDisabled={isBusy || removal === null}
          isLoading={props.isSaving && lastSave === "removeRow"}
          leftIcon={<FiMinus />}
          size="sm"
          variant="outline"
          onClick={() => {
            if (removal !== null && removal.writes.length > 0) {
              setIsConfirmingRemove(true);
            } else {
              void removeLastRow();
            }
          }}
        >
          Remove last row
        </Button>
        {/* An open cell may hold unsaved input: it is saved or cancelled first, never dropped */}
        <Box ml="auto">
          <Tooltip
            hasArrow
            shouldWrapChildren
            isDisabled={openCell === null}
            label="Save or cancel the open cell first"
          >
            <Button
              colorScheme="blue"
              isDisabled={props.isSaving || openCell !== null}
              size="sm"
              onClick={props.onCancel}
            >
              Done
            </Button>
          </Tooltip>
        </Box>
      </Flex>

      <ConfirmDialog
        body={`Row ${rows.length} and its answers will be deleted from this document.`}
        colorScheme="red"
        confirmLabel="Remove row"
        isOpen={isConfirmingRemove}
        title="Remove the last row?"
        onCancel={() => setIsConfirmingRemove(false)}
        onConfirm={() => {
          setIsConfirmingRemove(false);
          void removeLastRow();
        }}
      />
    </Box>
  );
}
